/**
 * PngDBMap
 *
 * Main class that orchestrates the rendering of png-db
 * data onto a Leaflet map.
 */

import path from 'path';

import DataMap from './core/data-map';
import defaultConfig from './core/config';
import { noop, uuidv4 } from './core/utils';
import SpatialReader from './spatial-reader';
import IndexedPngDB from './core/indexed-png-db';
import ShaderLayer from './shaders/shader-layer';


export default class PngDBMap {

    constructor(mnt = 'body', config = {}, resolver = noop) {
        const mount = document.querySelector(mnt);
        if (!mount) {
            throw new Error(`${mnt} does not exist in the document`);
            return
        }

        this.id = uuidv4();

        mount.dataset.pdbmMount = this.id;

        this.DOMNode = document.createElement('div');
        this.DOMNode.id = `pdbm-${this.id}`;
        this.DOMNode.classList.add('pdbm');
        mount.appendChild(this.DOMNode);

        this.config = this.__configure(config);

        if (this.config.marker) {
            this.DOMNode.classList.add('pdbm-with-marker');
        }

        this.db = new IndexedPngDB(this.config);
        this.map = new DataMap(this.DOMNode.id, this.config);

        this.listener = noop;
        this._debounce = null;

        this.__loaded = false;
        this.__load(resolver.bind(null, this));
    }


    /**
     * @param Object config
     * @return Object
     */
    __configure(config) {
        if (!('fields' in config)) {
            throw new Error('You must specify fields to load from the db');
            return;
        }

        const conf = { fields: config.fields };

        conf.fields.all = [config.fields.primary, ...(config.fields.secondary || [])];

        if ('palette' in config) {
            const palettes = Object.values(config.palette);

            if (palettes.some(p => typeof p === 'string'))  {
                if (palettes.some(p => typeof p !== 'string')) {
                    throw new Error('you can either configure one palette or multiple, not both');
                }

                config.palette = { default: { ...config.palette } };
            }
        }

        if ('path' in config) {
            conf.dbName = path.basename(config.path, '.json');
            conf.basePath = path.dirname(config.path).split('/');
            conf.basePath = conf.basePath.slice(0, conf.basePath.length - 1).join('/');

            delete config.path;
        }

        if ('marker' in config) {
            if (!config.marker) {
                conf.marker = false;
            }
            else {
                conf.marker = { ...defaultConfig.marker, ...config.marker };
            }
        }

        if ('shader' in config) {
            conf.shader = { ...defaultConfig.shader, ...config.shader };
        }

        return { ...defaultConfig, ...config, ...conf };
    }


    /**
     * @param Function resolve
     * @return Promise
     */
    async __load(resolve) {
        await this.db.loadDataFiles();

        this.shader = new ShaderLayer(this.db, this.config);
        this.reader = new SpatialReader(this.db, this.shader, this.config);

        this.map.addLayer(this.shader.tileLayer);

        this.__loaded = true;
        resolve();
    }


    _callListener() {
        const latLng = this.map.marker.getLatLng();

        this.listener({
            attributes: this.config.marker,
            data: this.reader.readSpace(latLng),
            latLng,
        });
    }


    _debouncer() {
        clearTimeout(this._debounce);

        this._debounce = setTimeout(() => {
            this._callListener();
        }, this.config.debounce);
    }


    /**
     * @param Function listener
     */
    listen(listener) {
        this.listener = listener;
        this.map.setEventListener(this._debouncer.bind(this));

        // Give the caller the initial state
        setTimeout(() => {
            this._callListener();
        }, 1);
    }


    /**
     * @param {Number|boolean} debounce
     */
    setDebounce(debounce) {
        if (typeof debounce === 'boolean' && debounce) {
            debounce = defaultConfig.debounce;
        }

        this.config.debounce = debounce;
    }


    /**
     * @param Object layers
     */
    setLayers(layers) {
        this.shader.setLayers(layers);
    }


    /**
     * @param String name
     */
    setPalette(name) {
        this.shader.setPalette(name);
    }


    /**
     * @param Object props
     */
    update(props) {
        this.shader.setUniformProperties(props);
        this._callListener();
    }

}
